import { Suspense, useEffect } from "react";
import { createBrowserRouter, Navigate, Outlet, useNavigate } from "react-router";
import { ProtectedRoute } from "@/features/auth/protected-route";
import { LoginPage } from "@/features/auth/login-page";
import { NotFoundPage } from "@/app/not-found-page";
import {
  AppShell,
  AuthenticatedLayout,
  DetailShellLayout,
  NoShellLayout,
} from "@/components/app-shell";
import { Skeleton } from "@/components/ui/skeleton";
import { registerNavigator, unregisterNavigator } from "@/app/auth-bridge";
import { lazyWithRetry } from "@/app/chunk-recovery";
import { RouteErrorBoundary } from "@/app/route-error-boundary";
import PropertyListPage from "@/features/property/property-list-page";
import PropertyDetailPage from "@/features/property/property-detail-page";
import PropertyCreatePage from "@/features/property/property-create-page";
import PropertyEditPage from "@/features/property/property-edit-page";
import LeadKanbanPage from "@/features/lead/lead-kanban-page";
import CustomerPage from "@/features/customer/customer-page";
import AppointmentCalendarPage from "@/features/appointment/appointment-calendar-page";

// Lazy-loaded routes — these contain the heavy dependencies (recharts
// for dashboard, xlsx for import) or rarely-hit code (settings, public
// share, password reset) which we keep out of the initial chunk.
const DashboardPage = lazyWithRetry(() => import("@/features/dashboard/dashboard-page"));
// Lead detail is moderately heavy (activity history dialog, broker
// popover, appointment form). Lazy-loaded so the kanban list page stays
// fast on first paint — users only pay for the page weight on the deep
// route.
const LeadDetailPage = lazyWithRetry(() => import("@/features/lead/lead-detail-page"));
// Appointment detail — same pattern as lead detail. The page bundles
// the appointment edit form + activity timeline, which the calendar
// page doesn't need; lazy so the calendar stays slim.
const AppointmentDetailPage = lazyWithRetry(
  () => import("@/features/appointment/appointment-detail-page"),
);
const PropertyImportPage = lazyWithRetry(
  () => import("@/features/property/property-import-page"),
);
const SettingsPage = lazyWithRetry(() => import("@/features/settings/settings-page"));
const NotificationsPage = lazyWithRetry(
  () => import("@/features/notification/notifications-page"),
);
const ViewingInboxPage = lazyWithRetry(
  () => import("@/features/appointment/viewing-inbox-page"),
);
const ForgotPasswordPage = lazyWithRetry(
  () => import("@/features/auth/forgot-password-page"),
);
const ResetPasswordPage = lazyWithRetry(
  () => import("@/features/auth/reset-password-page"),
);
const PublicPropertyPage = lazyWithRetry(
  () => import("@/features/public/public-property-page"),
);

function LazyRouteFallback() {
  return (
    <div
      className="flex flex-col gap-4 sm:gap-6"
      aria-busy="true"
      aria-label="Đang tải trang"
    >
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

/**
 * Root layout that registers the React Router navigator with the
 * auth-bridge module so the (router-agnostic) api-client can redirect
 * to /login on refresh failure without importing react-router.
 */
function RootLayout() {
  const navigate = useNavigate();
  useEffect(() => {
    registerNavigator((path) => navigate(path, { replace: true }));
    return () => unregisterNavigator();
  }, [navigate]);
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    // Without an errorElement, createBrowserRouter shows its own developer
    // page when a lazy chunk 404s after a redeploy. This reloads to the fresh
    // build instead (see RouteErrorBoundary / chunk-recovery).
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/",
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/forgot-password",
        element: (
          <Suspense fallback={<LazyRouteFallback />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "/reset-password",
        element: (
          <Suspense fallback={<LazyRouteFallback />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "/public/p/:token",
        element: (
          <Suspense fallback={<LazyRouteFallback />}>
            <PublicPropertyPage />
          </Suspense>
        ),
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            // AuthenticatedLayout supplies the command-palette context to
            // every authenticated route — both shell and shell-less.
            element: <AuthenticatedLayout />,
            children: [
              // Deep detail pages — no sidebar, no tab bar, full-bleed.
              // Each page renders its own back-button + breadcrumb.
              {
                element: <NoShellLayout />,
                children: [
                  {
                    path: "/properties/:id",
                    element: <PropertyDetailPage />,
                  },
                ],
              },
              // Deep detail pages that keep the desktop nav (sidebar) but stay
              // full-bleed on mobile (own back button + sticky bottom CTA).
              {
                element: <DetailShellLayout />,
                children: [
                  {
                    path: "/leads/:id",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <LeadDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/appointments/:id",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <AppointmentDetailPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              // Standard authenticated chrome — sidebar + tab bar.
              {
                element: <AppShell />,
                children: [
                  {
                    path: "/dashboard",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <DashboardPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/properties",
                    element: <PropertyListPage />,
                  },
                  {
                    path: "/properties/new",
                    element: <PropertyCreatePage />,
                  },
                  {
                    path: "/properties/import",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <PropertyImportPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/properties/:id/edit",
                    element: <PropertyEditPage />,
                  },
                  {
                    path: "/customers",
                    element: <CustomerPage />,
                  },
                  {
                    path: "/leads",
                    element: <LeadKanbanPage />,
                  },
                  {
                    path: "/appointments",
                    element: <AppointmentCalendarPage />,
                  },
                  {
                    path: "/notifications",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <NotificationsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/inbox",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <ViewingInboxPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/settings",
                    element: (
                      <Suspense fallback={<LazyRouteFallback />}>
                        <SettingsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);
